import { asset } from "./assets";

interface LocalizedText {
  en: string;
  hi: string;
}

/**
 * Home page content, verbatim from the old site's `/` and its Hindi twin
 * `/home-HI` (fetched directly; the Hindi page's text nodes each carry
 * `lang="hi-x-mtfrom-en"`, the site's own auto-translate marker - the same
 * source Task 2 used for `/inspiration-HI`). Card order is confirmed by the
 * old site's image filenames, which match the `asset()` URLs below one for
 * one. Where the old Hindi page left a piece of copy untranslated (four of
 * the five testimonials, the "More about" partner links), `hi` repeats the
 * English value rather than inventing a translation.
 */
export const HOME = {
  meditation: [
    {
      eyebrow: { en: "Daily Practice", hi: "दैनिक अभ्यास" },
      title: {
        en: "Daily Meditation Practice - Online",
        hi: "दैनिक ध्यान अभ्यास - ऑनलाइन",
      },
      lines: [
        {
          en: "Mon-Fri: 6am, 7am, 9pm",
          hi: "सोमवार-शुक्रवार: सुबह 6 बजे, 7 बजे, रात 9 बजे",
        },
        {
          en: "Sat: 8am, 9pm; Sun: 8am, 9am (kids), 9pm",
          hi: "शनिवार: सुबह 8 बजे, रात 9 बजे; रविवार : सुबह 8 बजे, सुबह 9 बजे (बच्चे), रात 9 बजे",
        },
      ],
      cta: {
        label: { en: "Learn More", hi: "और अधिक जानें" },
        href: "/activities/daily-practice",
      },
      image: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/768/464305d81e794a37af19fc37387aadeb.jpg?v=250703010154",
      ),
    },
    {
      eyebrow: { en: "Learn How to Meditate", hi: "ध्यान करना सीखें" },
      title: {
        en: "Learn Meditation before the Practice",
        hi: "अभ्यास से पहले ध्यान सीखें",
      },
      lines: [
        { en: "Anyday, Anytime", hi: "किसी भी दिन, किसी भी समय" },
        { en: "Fix an Appointment", hi: "अपॉइंटमेंट तय करें" },
      ],
      cta: {
        label: { en: "Book Appointment", hi: "अपॉइंटमेंट बुक करें" },
        href: "https://learnvymeditation.setmore.com/",
        external: true,
      },
      image: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/768/df9689e037cc40209d77fa74994da29e.jpg?v=250703010154",
      ),
    },
    {
      eyebrow: { en: "Meditation Challenge", hi: "ध्यान चुनौती" },
      title: {
        en: "Global Meditation Challenge",
        hi: "वैश्विक ध्यान चुनौती",
      },
      lines: [
        {
          en: "An Annual event to participate by anyone from anywhere in the world.",
          hi: "यह एक वार्षिक आयोजन है जिसमें विश्व के किसी भी स्थान से कोई भी भाग ले सकता है।",
        },
      ],
      cta: {
        label: { en: "Learn More", hi: "और अधिक जानें" },
        href: "/events/meditation-challenge",
      },
      image: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/768/c1e53979bb7f41b18d2f71726549e1b7.jpg?v=250703010154",
      ),
    },
  ],
  quotes: [
    {
      en: "Life, as it is, is the reservoir of all kinds of experiences. Just be there to feel the beat of life, never to overwhelm YOURSELF.",
      hi: "जीवन, जैसा कि यह है, सभी प्रकार के अनुभवों का भंडार है। बस जीवन की धड़कन को महसूस करने के लिए मौजूद रहें, कभी भी खुद को अभिभूत न करें।",
    },
    {
      en: "When you do what you must do, what you do appears to be difficult: trying to sway you somewhere not worthy of you just to itself acknowledge that ‘all that is not true to your highest self’ is not what you are meant for.",
      hi: "जब आप वह करते हैं जो आपको करना चाहिए, तो आप जो करते हैं वह कठिन प्रतीत होता है: आपको किसी ऐसे स्थान पर ले जाने का प्रयास करना जो आपके योग्य नहीं है, केवल यह स्वीकार करने के लिए कि 'वह सब जो आपके सर्वोच्च स्व के लिए सत्य नहीं है' वह वह नहीं है जिसके लिए आप बने हैं।",
    },
    {
      en: "Surrendering all emotions to become an observer, a separate entity to see the emotions flow through—'never become the emotions' is the first step towards finding your true self.",
      hi: "सभी भावनाओं को समर्पित कर एक पर्यवेक्षक बन जाना, भावनाओं को प्रवाहित होते देखने के लिए एक अलग इकाई बन जाना - 'कभी भी भावनाएं न बनें' अपने सच्चे स्व को खोजने की दिशा में पहला कदम है।",
    },
    {
      en: "Surrender to the higher design in the making of you. Being 'taken over' by some higher power is pure acceptance of ‘what is’, which is pure bliss in the midst of all chaos.",
      hi: "अपने निर्माण में उच्चतर डिजाइन के प्रति समर्पण करें। किसी उच्चतर शक्ति द्वारा 'अधिकार प्राप्त' होना 'जो है' उसे शुद्ध रूप से स्वीकार करना है, जो सभी अराजकता के बीच शुद्ध आनंद है।",
    },
    {
      en: "An inscribed doctrine is the faith in the values of humanity; the sanctity of which is undeterred not by the faith in humanity but by perseverance in the values of humanity.",
      hi: "एक अंतर्निहित सिद्धांत मानवता के मूल्यों में विश्वास है; जिसकी पवित्रता मानवता में विश्वास से नहीं, बल्कि मानवता के मूल्यों में दृढ़ता से अप्रभावित होती है।",
    },
    {
      en: "Pleasure is usually impulsive where waves of emotion overlap, one after the other. Pain usually follows the same pattern as pleasure, ‘the result of undisciplined dispositions’.",
      hi: "आनंद आमतौर पर आवेगपूर्ण होता है, जहाँ भावनाओं की लहरें एक के बाद एक ओवरलैप होती हैं। दर्द आमतौर पर आनंद के समान ही पैटर्न का अनुसरण करता है, 'अनुशासनहीन स्वभाव का परिणाम'।",
    },
  ],
  /**
   * The old site only ran its auto-translate widget over the first
   * testimonial (Devang Merchant's); the other four stayed English on
   * `/home-HI` verbatim, so their `hi` repeats the English text.
   */
  testimonials: [
    {
      name: "Sudan Wargantiwar",
      place: "Singapore",
      text: {
        en: "I started attending Vihangam Yoga & meditation sessions in 2019. Though I was not consistent in attending daily sessions initially, I decided to give time to myself. The breathing exercises and meditation are helping me to keep physically and mentally fit. I used to get chronic cluster headaches, which had stopped now. Also, despite having 5 to 5.30 hrs. of sleep, since I started meditation never felt tired or exhausted. I salute to Vihangam Singapore team for their dedication and sacrifices to conduct daily morning and evening sessions without fail.",
        hi: "I started attending Vihangam Yoga & meditation sessions in 2019. Though I was not consistent in attending daily sessions initially, I decided to give time to myself. The breathing exercises and meditation are helping me to keep physically and mentally fit. I used to get chronic cluster headaches, which had stopped now. Also, despite having 5 to 5.30 hrs. of sleep, since I started meditation never felt tired or exhausted. I salute to Vihangam Singapore team for their dedication and sacrifices to conduct daily morning and evening sessions without fail.",
      },
    },
    {
      name: "Devang Merchant",
      place: "Malaysia",
      text: {
        en: 'I used to sleep with a lot of thoughts in my head and invariably never felt relaxed in the morning but once I started meditation in the morning it became a way of life, and my day is now calm & composed. It is rightly said that "It is all in the mind" and once you can control your mind, you are in control of your life. I encourage everyone to join the Vihangam session and feel the transformation from within.',
        hi: 'मैं अपने दिमाग में बहुत सारे विचारों के साथ सोता था और हमेशा सुबह आराम महसूस नहीं करता था, लेकिन एक बार जब मैंने सुबह ध्यान करना शुरू किया तो यह जीवन का एक तरीका बन गया, और मेरा दिन अब शांत और संयमित रहता है। यह सही कहा गया है कि "यह सब मन में है" और एक बार जब आप अपने मन को नियंत्रित कर लेते हैं, तो आप अपने जीवन को नियंत्रित कर लेते हैं। मैं सभी को विहंगम सत्र में शामिल होने और भीतर से परिवर्तन महसूस करने के लिए प्रोत्साहित करता हूँ।',
      },
    },
    {
      name: "Vallamkonda Vijayakumar",
      place: "Singapore",
      text: {
        en: "I have experienced a great difference in my focus and a reduction in my stress levels, especially with meditation and breathing exercises. I have also been introduced to my wife and friends, and they are seeing the benefit too. It’s become a regular activity in my daily life. A BIG thanks to Vihangam Yoga.",
        hi: "I have experienced a great difference in my focus and a reduction in my stress levels, especially with meditation and breathing exercises. I have also been introduced to my wife and friends, and they are seeing the benefit too. It’s become a regular activity in my daily life. A BIG thanks to Vihangam Yoga.",
      },
    },
    {
      name: "Santha Kumari",
      place: "Malaysia",
      text: {
        en: "Firstly, meditation for me was a relaxation tool but with time it became second to nature. I cannot say for certain what one specific thing made meditation work for me. I had faith in Vihangam Yoga Meditation and with regular practice, I became calm, relaxed, stressed-free, and energetic. In many ways meditation has helped me change my attitude towards life and gave me peace of mind and happiness. I also realize I have a better understanding of myself and others, too. Sometimes it's a challenge to FOCUS my mind on one thing but I have learned that with discipline and constant practice, I become still, and this is a beautiful moment. After meditation each day, I feel so calm, content, relaxed, and full of gratitude. My heartfelt gratitude to VY Meditation.",
        hi: "Firstly, meditation for me was a relaxation tool but with time it became second to nature. I cannot say for certain what one specific thing made meditation work for me. I had faith in Vihangam Yoga Meditation and with regular practice, I became calm, relaxed, stressed-free, and energetic. In many ways meditation has helped me change my attitude towards life and gave me peace of mind and happiness. I also realize I have a better understanding of myself and others, too. Sometimes it's a challenge to FOCUS my mind on one thing but I have learned that with discipline and constant practice, I become still, and this is a beautiful moment. After meditation each day, I feel so calm, content, relaxed, and full of gratitude. My heartfelt gratitude to VY Meditation.",
      },
    },
    {
      name: "Stelios Minas",
      place: "Singapore",
      text: {
        en: "Regular practice of Vihangam Yoga has greatly contributed to the reduction of stress levels and has helped me maintain the focus on daily tasks and boost my productivity. A truly wonderful and enlightening experience.",
        hi: "Regular practice of Vihangam Yoga has greatly contributed to the reduction of stress levels and has helped me maintain the focus on daily tasks and boost my productivity. A truly wonderful and enlightening experience.",
      },
    },
  ],
  about: {
    title: { en: "About Us", hi: "हमारे बारे में" },
    body: {
      en: "Vihangam Yoga Singapore is an independent & autonomous body registered organization. We are dedicated to serving the society for mental & spiritual upliftment so that every individual can have a balanced, stress-free, peaceful, happy life. We specialized in Meditation techniques, regular practice of which empowers anyone by shifting his/her current base to a higher one. During the COVID period, we served society by conducting 1,500+ meditation sessions. We don't differentiate on the basis of race & religion. All humans are the same!",
      hi: "विहंगम योग सिंगापुर एक स्वतंत्र और स्वायत्त निकाय पंजीकृत संगठन है। हम मानसिक और आध्यात्मिक उत्थान के लिए समाज की सेवा करने के लिए समर्पित हैं ताकि हर व्यक्ति संतुलित, तनाव मुक्त, शांतिपूर्ण, खुशहाल जीवन जी सके। हम ध्यान तकनीकों में विशेषज्ञ हैं, जिसका नियमित अभ्यास किसी भी व्यक्ति को उसके वर्तमान आधार को उच्चतर स्तर पर ले जाकर सशक्त बनाता है। कोविड अवधि के दौरान , हमने 1,500+ ध्यान सत्र आयोजित करके समाज की सेवा की। हम जाति और धर्म के आधार पर भेदभाव नहीं करते हैं। सभी मनुष्य एक जैसे हैं!",
    },
    emphasis: {
      en: ["During the COVID period", "1,500+ meditation sessions"],
      hi: ["कोविड अवधि के दौरान", "1,500+ ध्यान सत्र"],
    },
    cta: {
      label: {
        en: "Click for Our Inspiration",
        hi: "हमारी प्रेरणा के लिए क्लिक करें",
      },
      href: "/inspiration",
    },
  },
  /**
   * The four section headings the old `/home-HI` translated (verbatim). The
   * English matches the old `/home` exactly; the hero, "Upcoming" and "From
   * our gatherings" headings are this rebuild's own copy and stay English.
   */
  sections: {
    meditation: { en: "Meditation", hi: "ध्यान" },
    quotes: {
      en: "Quotes (by Naamdeo ji)",
      hi: "उद्धरण (नामदेव जी द्वारा)",
    },
    testimonials: { en: "Testimonials", hi: "प्रशंसापत्र" },
    moreAbout: { en: "More about", hi: "अधिक जानकारी" },
  },
  /** The old site never translated these three partner-site labels. */
  moreAbout: [
    { label: "Vihangam Yoga, India", href: "https://vihangamyoga.org" },
    { label: "Vihangam Yoga, USA", href: "http://us.vihangamyoga.org" },
    { label: "VOGOF", href: "https://www.vihangamyoga.org/vygof/" },
  ],
  /**
   * The old site's homepage poster carousel, complete: all nine posters, in
   * its own order, with its own alt text (the two it left undescribed are
   * described here from the artwork). Three of them were 9-20MB animated
   * GIFs, far past the asset pipeline's cap, so they ship as a representative
   * still frame instead - the originals are archived outside the repo.
   */
  posters: [
    {
      src: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/675-380-675-375-0-2/a13bb2b76b8845049db70df098d1109a.jpg?v=250703010154",
      ),
      alt: "Annual Blood Donation",
    },
    {
      src: "/media/334eacf03c304ecb9cf504f63ca29aea-75a2fd41.jpg",
      alt: "14th (GOSAP) Global Online Swarved Akhand Path",
    },
    {
      src: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/675-380-675-375-0-2/c29aa4b7a39e4754bd600c6db916ee4f.jpg?v=250703010154",
      ),
      alt: "Kids' Meditation",
    },
    {
      src: "/media/86dd156bea0747fa856a80abc6469055-f40bc15d.jpg",
      alt: "Outdoor Activity",
    },
    {
      src: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/675-380-675-375-0-2/69ae6712cbdb42e2849186e207b76212.gif?v=250703010154",
      ),
      alt: "International Day of Yoga",
    },
    {
      src: "/media/473186149cc445efaa788473a588cbba-23d77938.jpg",
      alt: "Satsang",
    },
    {
      src: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/675-380-675-375-0-2/534711c788dc4eba8588a2194d64a4ce.gif?v=250703010154",
      ),
      alt: "Meditation is not imagination, thinking or movement of the mind",
    },
    {
      src: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/675-380-675-375-0-2/5f9cf246b4a741debcfa7e8aaf9c5a11.gif?v=250703010154",
      ),
      alt: "Vihangam Meditation: come, let's calm the mind",
    },
    {
      src: asset(
        "https://site-hjx4qqt6.wsecdn1.websitecdn.com/uploads/r/675-375-675-375-0-0/e055338776c14470bbdd2d9e5e8872b9.jpg?v=250703010154",
      ),
      alt: "5 Stages of Meditation",
    },
  ],
} satisfies {
  meditation: readonly {
    eyebrow: LocalizedText;
    title: LocalizedText;
    lines: readonly LocalizedText[];
    cta: { label: LocalizedText; href: string; external?: boolean };
    image: string;
  }[];
  quotes: readonly LocalizedText[];
  testimonials: readonly {
    name: string;
    place: string;
    text: LocalizedText;
  }[];
  about: {
    title: LocalizedText;
    body: LocalizedText;
    emphasis: { en: readonly string[]; hi: readonly string[] };
    cta: { label: LocalizedText; href: string };
  };
  sections: {
    meditation: LocalizedText;
    quotes: LocalizedText;
    testimonials: LocalizedText;
    moreAbout: LocalizedText;
  };
  moreAbout: readonly { label: string; href: string }[];
  posters: readonly { src: string; alt: string }[];
};
